// Small, dependency-free statistics for the survey dashboard (SPSS-like tests).
// Implements the numeric pieces we need: chi-square test of independence with a
// real p-value, Cramér's V effect size, and Pearson correlation with a t-test
// p-value. p-values use standard incomplete gamma / beta approximations
// (Numerical Recipes), accurate enough for reporting significance.

// ── Special functions ────────────────────────────────────────────────────────
function lnGamma(x: number): number {
  const c = [76.18009172947146, -86.50532032941677, 24.01409824083091,
    -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5];
  let y = x;
  let tmp = x + 5.5;
  tmp -= (x + 0.5) * Math.log(tmp);
  let ser = 1.000000000190015;
  for (let j = 0; j < 6; j++) { y += 1; ser += c[j] / y; }
  return -tmp + Math.log((2.5066282746310005 * ser) / x);
}

// Regularized lower incomplete gamma P(a,x).
function gammaP(a: number, x: number): number {
  if (x <= 0 || a <= 0) return 0;
  if (x < a + 1) {
    // series
    let ap = a, sum = 1 / a, del = sum;
    for (let n = 0; n < 200; n++) {
      ap += 1; del *= x / ap; sum += del;
      if (Math.abs(del) < Math.abs(sum) * 1e-12) break;
    }
    return sum * Math.exp(-x + a * Math.log(x) - lnGamma(a));
  }
  return 1 - gammaQcf(a, x);
}
// Continued-fraction complement Q(a,x), used when x >= a+1.
function gammaQcf(a: number, x: number): number {
  const tiny = 1e-30;
  let b = x + 1 - a, c = 1 / tiny, d = 1 / b, h = d;
  for (let i = 1; i < 200; i++) {
    const an = -i * (i - a);
    b += 2;
    d = an * d + b; if (Math.abs(d) < tiny) d = tiny;
    c = b + an / c; if (Math.abs(c) < tiny) c = tiny;
    d = 1 / d; const del = d * c; h *= del;
    if (Math.abs(del - 1) < 1e-12) break;
  }
  return Math.exp(-x + a * Math.log(x) - lnGamma(a)) * h;
}
function gammaQ(a: number, x: number): number { return 1 - gammaP(a, x); }

// Regularized incomplete beta I_x(a,b).
function betacf(a: number, b: number, x: number): number {
  const tiny = 1e-30;
  let qab = a + b, qap = a + 1, qam = a - 1;
  let c = 1, d = 1 - (qab * x) / qap;
  if (Math.abs(d) < tiny) d = tiny;
  d = 1 / d; let h = d;
  for (let m = 1; m <= 200; m++) {
    const m2 = 2 * m;
    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
    d = 1 + aa * d; if (Math.abs(d) < tiny) d = tiny;
    c = 1 + aa / c; if (Math.abs(c) < tiny) c = tiny;
    d = 1 / d; h *= d * c;
    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
    d = 1 + aa * d; if (Math.abs(d) < tiny) d = tiny;
    c = 1 + aa / c; if (Math.abs(c) < tiny) c = tiny;
    d = 1 / d; const del = d * c; h *= del;
    if (Math.abs(del - 1) < 1e-12) break;
  }
  return h;
}
function betai(a: number, b: number, x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const bt = Math.exp(lnGamma(a + b) - lnGamma(a) - lnGamma(b) + a * Math.log(x) + b * Math.log(1 - x));
  return x < (a + 1) / (a + b + 2) ? (bt * betacf(a, b, x)) / a : 1 - (bt * betacf(b, a, 1 - x)) / b;
}

// ── Public API ───────────────────────────────────────────────────────────────
export interface ChiSquareResult {
  chi2: number; df: number; p: number; cramersV: number; n: number; valid: boolean;
}

// Chi-square test of independence on a contingency matrix (rows × cols of counts).
export function chiSquareTest(matrix: number[][]): ChiSquareResult {
  const r = matrix.length;
  const c = r > 0 ? matrix[0].length : 0;
  const rowSums = matrix.map((row) => row.reduce((a, b) => a + b, 0));
  const colSums = Array.from({ length: c }, (_, j) => matrix.reduce((a, row) => a + row[j], 0));
  const n = rowSums.reduce((a, b) => a + b, 0);
  const df = (r - 1) * (c - 1);
  if (n === 0 || df <= 0) return { chi2: 0, df, p: 1, cramersV: 0, n, valid: false };
  let chi2 = 0;
  for (let i = 0; i < r; i++) {
    for (let j = 0; j < c; j++) {
      const exp = (rowSums[i] * colSums[j]) / n;
      if (exp > 0) chi2 += ((matrix[i][j] - exp) ** 2) / exp;
    }
  }
  const p = gammaQ(df / 2, chi2 / 2);
  const cramersV = Math.sqrt(chi2 / (n * Math.min(r - 1, c - 1)));
  return { chi2, df, p, cramersV, n, valid: true };
}

export interface PearsonInput { n: number; sumX: number; sumY: number; sumXY: number; sumX2: number; sumY2: number; }
export interface PearsonResult { r: number; p: number; n: number; valid: boolean; }

// Pearson correlation from moment sums (computed server-side over the full dataset),
// with a two-tailed t-test p-value.
export function pearsonFromSums(s: PearsonInput): PearsonResult {
  const { n, sumX, sumY, sumXY, sumX2, sumY2 } = s;
  if (!n || n < 3) return { r: 0, p: 1, n: n || 0, valid: false };
  const cov = sumXY - (sumX * sumY) / n;
  const vx = sumX2 - (sumX * sumX) / n;
  const vy = sumY2 - (sumY * sumY) / n;
  if (vx <= 0 || vy <= 0) return { r: 0, p: 1, n, valid: false };
  let r = cov / Math.sqrt(vx * vy);
  r = Math.max(-1, Math.min(1, r));
  const df = n - 2;
  const denom = 1 - r * r;
  const t = denom <= 0 ? Infinity : r * Math.sqrt(df / denom);
  const p = denom <= 0 ? 0 : betai(df / 2, 0.5, df / (df + t * t));
  return { r, p, n, valid: true };
}

// Human-readable significance label.
export function significance(p: number): string {
  if (p < 0.001) return 'p < 0.001 (highly significant)';
  if (p < 0.01) return 'p < 0.01 (very significant)';
  if (p < 0.05) return 'p < 0.05 (significant)';
  if (p < 0.1) return 'p < 0.10 (marginal)';
  return 'not significant';
}

export function effectStrength(v: number): string {
  const a = Math.abs(v);
  if (a < 0.1) return 'negligible';
  if (a < 0.3) return 'weak';
  if (a < 0.5) return 'moderate';
  return 'strong';
}
